import cx from 'classnames'
import { ReactNode, RefObject, useEffect, useState } from 'react'
import Styles from './LinkedinPreview.module.scss'
import { LinkedInIntegration } from 'api/clients/ExternalIntegrationsClient'
import { CommentIcon, LikeIcon, RepostIcon, SendIcon } from './icons'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { MoreButton } from '../components/MoreButton/MoreButton'
import { useTruncateText } from '../hooks/useTruncateText'
import { getIntegrationDetails } from '../getIntegrationDetails'
import { PollingPreview } from '../components/PollingPreview/PollingPreview'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { Skeleton } from 'antd'
import { Loading } from '../components/Loading/Loading'
import { highlightContent } from '../highlightContent'
import { ConnectButton } from 'components/ArticlePostPreview/ConnectButton/ConnectButton'
import { CaptionSkeleton } from '../components/CaptionSkeleton/CaptionSkeleton'
import { MediaObject } from 'types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Button } from 'blaze-ui/Button'
import { Edit1 } from 'blaze-ui/icons'
import * as CSS from 'csstype'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditCaptionOverlay } from '../components/EditCaptionOverlay/EditCaptionOverlay'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ImageGridMedia } from './ImageGridMedia'
import { CarouselMedia } from './CarouselMedia'

interface LinkedinPreviewProps {
  integration?: LinkedInIntegration
  content: string
  loading?: boolean
  mediaObjects?: MediaObject[]
  hideConnectButton?: boolean
  alwaysTruncateCaption?: boolean
  onEdit?: () => void
  containerClassName?: string
  customPreviewContent?: ReactNode
  maxWidth?: number | null
  showVideoControls?: boolean
  isActive?: boolean
  isInsertingMedia?: boolean
  isTransitioning?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  isGenerating?: boolean
  scheduledPost?: ScheduledPostShow
  onClose?: () => void
  postPreviewHeaderRef?: RefObject<HTMLDivElement>
  postPreviewContentRef?: RefObject<HTMLDivElement>
  postPreviewFooterRef?: RefObject<HTMLDivElement>
  onSeeMoreToggle?: (expanded: boolean) => void
  activePlatform?: ExternalIntegrationEnum
  carouselTitle?: string
}

export const LinkedinPreview = ({
  integration,
  content,
  mediaObjects,
  loading,
  hideConnectButton,
  onEdit,
  containerClassName,
  customPreviewContent,
  showVideoControls,
  maxWidth = 550,
  isActive = true,
  isInsertingMedia = false,
  isTransitioning = false,
  isGenerating = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  scheduledPost,
  onClose,
  alwaysTruncateCaption = false,
  postPreviewHeaderRef,
  postPreviewContentRef,
  postPreviewFooterRef,
  onSeeMoreToggle,
  activePlatform,
  carouselTitle,
}: LinkedinPreviewProps) => {
  const { name, picture } = getIntegrationDetails(integration)
  const { isMobile } = useResponsiveDesign()
  const [imagesLoading, setImagesLoading] = useState(false)
  const isMultiImage = (mediaObjects?.length || 0) > 1
  const useCarouselLayout = !scheduledPost || (scheduledPost.created_at ?? '') > '2026-03-04'
  const isCarousel = isMultiImage && useCarouselLayout
  const isPostPreviewV2 = useFlipperFlag('post_preview_v2')
  const { isTruncated, setIsTruncated, shouldShowMoreButton, textRef } = useTruncateText({
    content,
    truncatedByDefault:
      alwaysTruncateCaption || ((!isMobile || isPostPreviewV2) && (!!mediaObjects?.length || !!customPreviewContent)),
  })

  useEffect(() => {
    if (activePlatform && activePlatform !== ExternalIntegrationEnum.LinkedIn && !isTruncated) {
      setIsTruncated(true)
    }
  }, [activePlatform, isTruncated, setIsTruncated])

  const highlightClassNames = {
    linkClassName: Styles.highlight,
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const style: CSS.Properties = {
    ['--max-width' as any]: `${maxWidth}px`,
  }

  return (
    <div className={cx(Styles.linkedinPost, containerClassName)} data-testid="linkedin-preview" style={style}>
      <div className={Styles.content}>
        <ConditionalWrapper
          condition={showHoverActionOverlay && almanacFileKey}
          wrapper={(children) => {
            if (!scheduledPost || !almanacFileKey) return children

            return (
              <EditCaptionOverlay
                scheduledPost={scheduledPost}
                almanacFileKey={almanacFileKey}
                borderRadius={0}
                position="top-right"
                onClose={onClose}
              >
                {children}
              </EditCaptionOverlay>
            )
          }}
        >
          <div className={Styles.profileSection} ref={postPreviewHeaderRef}>
            <img src={picture} alt={name} className={Styles.profilePicture} />
            <div className={Styles.info}>
              <span className={Styles.userName}>{name}</span>
              <div className={Styles.timestamp}>
                1m • Edited •{' '}
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <path
                    d="M7.75 1.57516C6.53859 1.57516 5.35439 1.93439 4.34713 2.60741C3.33988 3.28044 2.55483 4.23703 2.09124 5.35623C1.62765 6.47543 1.50636 7.70696 1.74269 8.89509C1.97903 10.0832 2.56238 11.1746 3.41897 12.0312C4.27557 12.8878 5.36694 13.4711 6.55507 13.7075C7.74321 13.9438 8.97474 13.8225 10.0939 13.3589C11.2131 12.8953 12.1697 12.1103 12.8428 11.103C13.5158 10.0958 13.875 8.91158 13.875 7.70016C13.875 6.07571 13.2297 4.5178 12.081 3.36914C10.9324 2.22048 9.37445 1.57516 7.75 1.57516ZM3.375 7.70016C3.375 6.75355 3.68203 5.83246 4.25 5.07516L4.73125 5.55642C4.85579 5.67849 4.95476 5.82416 5.02236 5.98491C5.08997 6.14566 5.12486 6.31827 5.125 6.49266V7.42892C5.12516 7.6029 5.1944 7.76971 5.3175 7.89266L5.8075 8.38266C5.93046 8.50577 6.09726 8.57501 6.27125 8.57516H6.875V9.17891C6.87516 9.3529 6.9444 9.51971 7.0675 9.64266L7.5575 10.1327C7.68061 10.2556 7.74985 10.4224 7.75 10.5964V12.0752C6.58968 12.0752 5.47688 11.6142 4.65641 10.7938C3.83594 9.97329 3.375 8.86049 3.375 7.70016ZM8.835 11.9264L10.585 9.77391C10.6633 9.67679 10.7123 9.55936 10.7263 9.43538C10.7402 9.3114 10.7185 9.18601 10.6638 9.07391L10.1563 8.05891C10.0955 7.9402 10.0004 7.84252 9.88334 7.77854C9.76631 7.71456 9.63275 7.68726 9.5 7.70016H6.875V7.53391C6.87584 7.45213 6.89913 7.37216 6.94234 7.30271C6.98555 7.23327 7.04701 7.17704 7.12 7.14016L7.4525 6.97392C7.54446 6.92635 7.64647 6.90152 7.75 6.90152C7.85353 6.90152 7.95555 6.92635 8.0475 6.97392L8.625 7.26266L8.9575 6.65017C9.02713 6.52117 9.06324 6.37675 9.0625 6.23017V5.48641C9.06304 5.31418 9.12868 5.14853 9.24625 5.02266L10.1825 4.06891C10.8848 4.53865 11.4361 5.20171 11.7698 5.978C12.1034 6.75428 12.2051 7.61057 12.0626 8.44341C11.9201 9.27625 11.5395 10.05 10.9667 10.6712C10.3939 11.2924 9.65356 11.7344 8.835 11.9439V11.9264Z"
                    fill="#8F98A9"
                  />
                </svg>
              </div>
            </div>
            {onEdit && (
              <div className={Styles.editButton}>
                <Button variant="tertiary" onPress={onEdit} frontIcon={Edit1}>
                  Edit
                </Button>
              </div>
            )}
            {!hideConnectButton && isMobile && <ConnectButton margin="-30px -10px 0 auto" />}
          </div>
          <div className={Styles.postContent} ref={postPreviewContentRef}>
            <>
              {(() => {
                if (isGenerating) return <CaptionSkeleton />
                if (loading) {
                  return (
                    <>
                      {isMobile ? (
                        <PollingPreview
                          setImagesLoading={setImagesLoading}
                          imagesLoading={imagesLoading}
                          channel={ChannelType.LinkedInPosts}
                        />
                      ) : (
                        <Loading />
                      )}
                    </>
                  )
                }
                return (
                  <>
                    <span
                      ref={textRef as React.RefObject<HTMLSpanElement>}
                      key={`caption-${content}`}
                      className={cx({ [Styles.truncated]: isTruncated })}
                      dangerouslySetInnerHTML={{
                        __html: highlightContent(content, highlightClassNames),
                      }}
                    />
                    {shouldShowMoreButton && !alwaysTruncateCaption && (
                      <MoreButton
                        isTruncated={isTruncated}
                        setIsTruncated={() => {
                          setIsTruncated(!isTruncated)
                          onSeeMoreToggle?.(isTruncated)
                        }}
                      />
                    )}
                  </>
                )
              })()}
            </>
          </div>
        </ConditionalWrapper>
        <div className={Styles.postFooter}>
          {customPreviewContent ? customPreviewContent : null}
          {!customPreviewContent && isCarousel && mediaObjects && (
            <CarouselMedia
              mediaObjects={mediaObjects}
              carouselTitle={carouselTitle}
              showVideoControls={showVideoControls}
              showHoverActionOverlay={showHoverActionOverlay}
              almanacFileKey={almanacFileKey}
              isActive={isActive}
              isGenerating={isGenerating}
              onClose={onClose}
            />
          )}
          {!customPreviewContent && !isCarousel && mediaObjects && (
            <ImageGridMedia
              mediaObjects={mediaObjects}
              showVideoControls={showVideoControls}
              showHoverActionOverlay={showHoverActionOverlay}
              almanacFileKey={almanacFileKey}
              isActive={isActive}
              isGenerating={isGenerating}
              onClose={onClose}
            />
          )}
          {(imagesLoading && !mediaObjects?.length) || isInsertingMedia ? (
            <Skeleton className={Styles.imagePreview} active paragraph={{ rows: 1 }} title={false} />
          ) : null}
          {!isPostPreviewV2 && (
            <div className={Styles.postActions} ref={postPreviewFooterRef}>
              <div className={Styles.action} data-testid="like-icon">
                <LikeIcon /> {!isMobile && 'Like'}
              </div>
              <div className={Styles.action} data-testid="comment-icon">
                <CommentIcon /> {!isMobile && 'Comment'}
              </div>
              <div className={Styles.action} data-testid="repost-icon">
                <RepostIcon /> {!isMobile && 'Repost'}
              </div>
              <div className={Styles.action} data-testid="send-icon">
                <SendIcon /> {!isMobile && 'Send'}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
